import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Building2, FolderOpen, Search, Star } from 'lucide-react';

import { usePanel } from 'hooks/usePanel';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { sirenOrSiretLabel } from 'shared/utils/sirenSiret';

import {
  EligibleChildPersonneMoraleDto,
  EligiblePersonneMoraleDto,
  addToPortefeuille,
  getEligibleChildren,
  getEligiblePartners,
} from '../api/portefeuillePartageApi';

type Tab = 'partners' | 'children';

interface Props {
  plateformeId: string;
  onSuccess: () => void;
}

interface Candidate {
  personneId: string;
  designation: string;
  numeroSiret?: string;
  kind: Tab;
}

export const AddToPortefeuilleForm: React.FC<Props> = ({ plateformeId, onSuccess }) => {
  const { setPanelActions, closePanel } = usePanel();
  const [activeTab, setActiveTab] = useState<Tab>('partners');
  const [search, setSearch] = useState('');
  const [partners, setPartners] = useState<EligiblePersonneMoraleDto[]>([]);
  const [children, setChildren] = useState<EligibleChildPersonneMoraleDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPersonneId, setSelectedPersonneId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchData = useCallback(
    async (term: string) => {
      setLoading(true);
      try {
        if (activeTab === 'partners') {
          setPartners(await getEligiblePartners(plateformeId, term || undefined));
        } else {
          setChildren(await getEligibleChildren(plateformeId, term || undefined));
        }
      } catch {
        setPartners([]);
        setChildren([]);
      } finally {
        setLoading(false);
      }
    },
    [activeTab, plateformeId],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchData(search), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, fetchData]);

  // Reset sélection quand on change d'onglet
  useEffect(() => {
    setSelectedPersonneId(null);
  }, [activeTab]);

  const currentList: Candidate[] = useMemo(() => {
    if (activeTab === 'partners') {
      return partners.map((p) => ({
        personneId: p.personneId,
        designation: p.designation,
        numeroSiret: p.numeroSiret,
        kind: 'partners' as Tab,
      }));
    }
    return children.map((c) => ({
      personneId: c.personneId,
      designation: c.designation,
      numeroSiret: c.numeroSiret,
      kind: 'children' as Tab,
    }));
  }, [activeTab, partners, children]);

  const selectedRow = useMemo(
    () => currentList.find((r) => r.personneId === selectedPersonneId),
    [currentList, selectedPersonneId],
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedRow) return;
    const res = await addToPortefeuille(plateformeId, selectedRow.personneId);
    if (!res.isSuccess) {
      toasterService.showToast({
        message: res.errorMessage ?? "Échec de l'ajout",
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
      return;
    }
    toasterService.showToast({
      message: `${selectedRow.designation} ajoutée au portefeuille.`,
      displayType: DisplayTypeEnum.Toaster,
      color: GenericColors.Green,
    });
    onSuccess();
    closePanel();
  }, [selectedRow, plateformeId, onSuccess, closePanel]);

  useEffect(() => {
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: !selectedPersonneId,
      onSubmit: handleSubmit,
    });
    return () => setPanelActions(undefined);
  }, [setPanelActions, selectedPersonneId, handleSubmit]);

  return (
    <div className="flex flex-col gap-4">
      {/* Tabs */}
      <div className="border-b border-gray-200 flex gap-1">
        <button
          type="button"
          onClick={() => setActiveTab('partners')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
            activeTab === 'partners'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Star size={14} className={activeTab === 'partners' ? 'fill-amber-400 text-amber-400' : ''} />
          Partenaires
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('children')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
            activeTab === 'children'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FolderOpen size={14} />
          Mes personnes morales
        </button>
      </div>

      <p className="text-xs text-gray-600">
        {activeTab === 'partners' ? (
          <>
            Référencez une <strong>PM principale d&apos;une autre plateforme</strong> comme
            partenaire dans votre portefeuille.
          </>
        ) : (
          <>
            Promouvez une <strong>PM privée que vous avez créée</strong> au niveau de votre
            plateforme. Elle deviendra visible par tous les partenaires de l&apos;OU.
          </>
        )}
      </p>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par désignation ou SIRET..."
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>

      <div className="max-h-96 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
        {loading && (
          <div className="p-6 text-center text-sm text-gray-500">Chargement…</div>
        )}
        {!loading && currentList.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-500">
            {activeTab === 'partners' ? (
              <>
                Aucune PM partenaire éligible.
                <br />
                <span className="text-xs">
                  Seules les PMs principales d&apos;une autre plateforme apparaissent ici.
                </span>
              </>
            ) : (
              <>
                Aucune PM privée à promouvoir.
                <br />
                <span className="text-xs">
                  Les PMs créées par votre cabinet et non encore partagées apparaîtront ici.
                </span>
              </>
            )}
          </div>
        )}
        {!loading &&
          currentList.map((pm) => (
            <button
              key={pm.personneId}
              type="button"
              onClick={() => setSelectedPersonneId(pm.personneId)}
              className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors ${
                selectedPersonneId === pm.personneId ? 'bg-primary/5' : ''
              }`}
            >
              <div className="relative w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="text-primary" size={16} />
                {pm.kind === 'partners' && (
                  <Star
                    size={9}
                    className="absolute -top-1 -left-1 fill-amber-400 text-amber-400 drop-shadow-sm"
                  />
                )}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {pm.designation}
                </span>
                <span className="text-xs text-gray-500">{sirenOrSiretLabel(pm.numeroSiret)}: {pm.numeroSiret || '—'}</span>
              </div>
              {selectedPersonneId === pm.personneId && (
                <span className="text-xs font-semibold text-primary uppercase">
                  Sélectionné
                </span>
              )}
            </button>
          ))}
      </div>
    </div>
  );
};
